import { useEffect, useMemo } from "react";
import { useMediaQuery } from "./useMediaQuery";
import { EventEmitter } from "client_legacy/ndaw/eventEmitter";
import { useForceRerender } from "./useForceRerender";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { Theme } from "client/Api/Branding/UserTheme/userTheme";

const themeChangedEventEmitter = new EventEmitter<"new-theme">();

export function useConfiguredTheme(): Theme {
	return useTheme(false);
};

export type EffectiveTheme = Exclude<Theme, Theme.System>;

export function useEffectiveTheme(): EffectiveTheme {
	return useTheme(true);
}

function useTheme(shouldResolveSystemTheme: true): Exclude<Theme, Theme.System>;
function useTheme(shouldResolveSystemTheme: false): Theme;
function useTheme(shouldResolveSystemTheme: boolean): Theme {
	const [rerender, themeVersion] = useForceRerender();

	const selectedThemeValue = useMemo(
		(): string => {
			hasImplicitDependencyOn(themeVersion);

			return document.documentElement
				.dataset["theme"] ?? "light";
		},
		[themeVersion]
	);

	const mediaQuery = useMemo(
		(): string => {
			hasImplicitDependencyOn(themeVersion);

			return getComputedStyle(document.documentElement)
				.getPropertyValue("--dark-theme-selector");
		},
		[themeVersion]
	);

	const doesUserPreferDarkTheme: boolean = useMediaQuery(mediaQuery);

	useEffect(
		(): EffectWithDestructorResult => {
			themeChangedEventEmitter.on(
				"new-theme",
				rerender
			);

			return () => themeChangedEventEmitter.off(
				"new-theme",
				rerender
			);
		},
		[rerender]
	);

	switch (selectedThemeValue) {
		case themeToValueMap[Theme.Light]:
			return Theme.Light;
		case themeToValueMap[Theme.Dark]:
			return Theme.Dark;
		case themeToValueMap[Theme.System]:
			return shouldResolveSystemTheme
				? doesUserPreferDarkTheme
					? Theme.Dark
					: Theme.Light
				: Theme.System;
		default:
			console.error("Theme is not specified!");
			return Theme.Light;
	}
}

export function setDocumentTheme(theme: Theme): void {
	document.documentElement
		.dataset["theme"] = themeToValueMap[theme];

	themeChangedEventEmitter.emit("new-theme");
}

const themeToValueMap: {[key in Theme]: string} = {
	[Theme.Light]: "light",
	[Theme.Dark]: "dark",
	[Theme.System]: "system"
};